import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ApplicationLog {
  id: number;
  message: string;
  level: string;
  timeStamp: string;
  exception?: string;
  userId?: number;
  employeeId?: string;
  requestPath?: string;
  logEvent?: string;
  employee?: LogEmployee;
}

export interface ApplicationLogListRequest {
  page?: number;
  pageSize?: number;
  level?: string;
  levels?: string[];
  search?: string;
  startDate?: string;
  endDate?: string;
  userId?: number;
  employeeId?: string;
  requestPath?: string;
  sortBy?: string;
  sortDirection?: string;
}

export interface LogEmployee {
  employeeId: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  emailAlias?: string;
  displayName: string;
}

export interface AuditLog {
  id: number;
  message: string;
  timeStamp: string;
  auditLogType: number;
  auditLogTypeName: string;
  userId?: number;
  employeeId?: string;
  databaseEntityType: number;
  databaseEntityTypeName: string;
  entityId: string;
  oldValues?: string;
  newValues?: string;
  logEvent?: string;
  employee?: LogEmployee;
}

export interface AuditLogListRequest {
  page?: number;
  pageSize?: number;
  auditLogType?: number;
  auditLogTypes?: number[];
  databaseEntityType?: number;
  databaseEntityTypes?: number[];
  search?: string;
  startDate?: string;
  endDate?: string;
  userId?: number;
  employeeId?: string;
  sortBy?: string;
  sortDirection?: string;
}

export interface EnumValue {
  value: number;
  name: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LogService {
  private http = inject(HttpClient);

  private readonly apiUrl = `${environment.apiUrl}/api/logs`;

  /**
   * Get paginated application logs
   */
  getApplicationLogs(request: ApplicationLogListRequest = {}): Observable<PagedResult<ApplicationLog>> {
    let params = new HttpParams();

    if (request.page) params = params.set('page', request.page.toString());
    if (request.pageSize) params = params.set('pageSize', request.pageSize.toString());
    if (request.level) params = params.set('level', request.level);
    if (request.levels && request.levels.length > 0) {
      params = params.set('levels', request.levels.join(','));
    }
    if (request.search) params = params.set('search', request.search);
    if (request.startDate) params = params.set('startDate', request.startDate);
    if (request.endDate) params = params.set('endDate', request.endDate);
    if (request.userId) params = params.set('userId', request.userId.toString());
    if (request.employeeId) params = params.set('employeeId', request.employeeId);
    if (request.requestPath) params = params.set('requestPath', request.requestPath);
    if (request.sortBy) params = params.set('sortBy', request.sortBy);
    if (request.sortDirection) params = params.set('sortDirection', request.sortDirection);

    return this.http.get<PagedResult<ApplicationLog>>(`${this.apiUrl}/application`, { params });
  }

  /**
   * Get a single application log by ID
   */
  getApplicationLogById(id: number): Observable<ApplicationLog> {
    return this.http.get<ApplicationLog>(`${this.apiUrl}/application/${id}`);
  }

  /**
   * Get available log levels
   */
  getLogLevels(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/application/levels`);
  }

  /**
   * Get paginated audit logs
   */
  getAuditLogs(request: AuditLogListRequest = {}): Observable<PagedResult<AuditLog>> {
    let params = new HttpParams();

    if (request.page) params = params.set('page', request.page.toString());
    if (request.pageSize) params = params.set('pageSize', request.pageSize.toString());
    if (request.auditLogType !== undefined && request.auditLogType !== null)
      params = params.set('auditLogType', request.auditLogType.toString());
    if (request.auditLogTypes && request.auditLogTypes.length > 0) {
      params = params.set('auditLogTypes', request.auditLogTypes.join(','));
    }
    if (request.databaseEntityType !== undefined && request.databaseEntityType !== null)
      params = params.set('databaseEntityType', request.databaseEntityType.toString());
    if (request.databaseEntityTypes && request.databaseEntityTypes.length > 0) {
      params = params.set('databaseEntityTypes', request.databaseEntityTypes.join(','));
    }
    if (request.search) params = params.set('search', request.search);
    if (request.startDate) params = params.set('startDate', request.startDate);
    if (request.endDate) params = params.set('endDate', request.endDate);
    if (request.userId) params = params.set('userId', request.userId.toString());
    if (request.employeeId) params = params.set('employeeId', request.employeeId);
    if (request.sortBy) params = params.set('sortBy', request.sortBy);
    if (request.sortDirection) params = params.set('sortDirection', request.sortDirection);

    return this.http.get<PagedResult<AuditLog>>(`${this.apiUrl}/audit`, { params });
  }

  /**
   * Get a single audit log by ID
   */
  getAuditLogById(id: number): Observable<AuditLog> {
    return this.http.get<AuditLog>(`${this.apiUrl}/audit/${id}`);
  }

  /**
   * Get available audit log types (Create, Read, Update, Delete)
   */
  getAuditLogTypes(): Observable<EnumValue[]> {
    return this.http.get<EnumValue[]>(`${this.apiUrl}/audit/types`);
  }

  /**
   * Get available database entity types
   */
  getDatabaseEntityTypes(): Observable<EnumValue[]> {
    return this.http.get<EnumValue[]>(`${this.apiUrl}/audit/entity-types`);
  }
}
